import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Logout from '@mui/icons-material/Logout';
import Settings from '@mui/icons-material/Settings';
import { Avatar, IconButton, Menu, MenuItem, Divider, ListItemIcon, ListItemText, Typography } from '@mui/material';
import './workflowLayout.css';
import IconRail from './IconRail';
import ClientProjectPanel from './ClientProjectPanel';
import ProjectWorkflowTabs from './ProjectWorkflowTabs';
import ProjectForm from '../projects/ProjectForm';
import NotificationBell from '../notifications/NotificationBell';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectWorkflow } from '../../contexts/ProjectWorkflowContext';
import { buildAdminRailItems, buildAgencyRailItems, buildClientRailItems } from './workflowNav';
import { clientService } from '../../services/clientService';
import { projectService } from '../../services/projectService';
import { priceChangeRequestService } from '../../services/priceChangeRequestService';
import { pendingSitesService } from '../../services/pendingSitesService';
import { competitorBacklinksService } from '../../services/competitorBacklinksService';
import { CreateProjectData, Project, UpdateProjectData } from '../../types';
import {
  completeProjectCreation,
  completeProjectDeletion,
  completeProjectUpdate,
} from '../../utils/workflowPanelCache';
import {
  consumePendingHomeLaunch,
  hasPendingHomeLaunch,
  prefersDashboard,
  clearPreferDashboard,
} from '../../utils/homeLaunch';
import { mediaUrl } from '../../utils/mediaUrl';
import { walletService } from '../../services/walletService';
import { rememberExploreSitesReturn } from '../../utils/catalogGateMessaging';
import { WALLET_ENABLED } from '../../config/productFlags';

interface WorkflowLayoutProps {
  children: React.ReactNode;
}

const WorkflowLayout: React.FC<WorkflowLayoutProps> = ({ children }) => {
  const { user, logout, isSuperAdmin, isAdmin, isAgency, isClient, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    selectedProject,
    activeStep,
    panelOpen,
    setPanelOpen,
    togglePanel,
    selectProject,
    focusClientInPanel,
    clearProject,
    workflowEntryPoint,
    requestHomeLaunch,
    patchProjectInSelection,
  } = useProjectWorkflow();

  const urlProjectId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('standalone') === '1') return null;
    return sp.get('projectId');
  }, [location.search]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const [projectFormClientId, setProjectFormClientId] = useState('');
  const [editingProject, setEditingProject] = useState<Project | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);

  const openProjectForm = (clientId?: string) => {
    setEditingProject(undefined);
    if (clientId) {
      setProjectFormClientId(clientId);
      focusClientInPanel(clientId);
    } else {
      setProjectFormClientId('');
    }
    setProjectFormOpen(true);
  };

  const openEditProjectForm = async () => {
    if (!selectedProject) return;
    setFormLoading(true);
    try {
      const full = await projectService.getProject(selectedProject.id);
      setEditingProject(full);
      setProjectFormClientId(full.clientId || selectedProject.clientId || '');
      setProjectFormOpen(true);
    } catch {
      // Still open the form with selection fields so settings remain reachable.
      setEditingProject({
        id: selectedProject.id,
        projectName: selectedProject.projectName,
        clientId: selectedProject.clientId,
      } as Project);
      setProjectFormClientId(selectedProject.clientId);
      setProjectFormOpen(true);
    } finally {
      setFormLoading(false);
    }
  };

  const closeProjectForm = () => {
    setProjectFormOpen(false);
    setProjectFormClientId('');
    setEditingProject(undefined);
  };

  useEffect(() => {
    const onEditProject = () => {
      void openEditProjectForm();
    };
    window.addEventListener('wf-edit-project', onEditProject);
    return () => window.removeEventListener('wf-edit-project', onEditProject);
  }, [selectedProject]);

  const clientIdForProjects = isClient ? user?.clientId : undefined;
  const isStaff = isAdmin || isSuperAdmin;

  const { data: clientProjectsData, isLoading: clientProjectsLoading } = useQuery({
    queryKey: ['wf-client-project-count', clientIdForProjects],
    queryFn: () =>
      projectService.getProjects({
        clientId: clientIdForProjects,
        limit: 1,
      }),
    enabled: Boolean(isClient && clientIdForProjects),
  });

  // Admin / Super Admin: wait for panel data before consuming post-login home launch
  const { isLoading: staffClientsLoading, isFetched: staffClientsFetched } = useQuery({
    queryKey: ['wf-clients', undefined, 0],
    queryFn: () => clientService.getClients({ limit: 500 }),
    enabled: isStaff,
    staleTime: 0,
  });
  const { isLoading: staffProjectsLoading, isFetched: staffProjectsFetched } = useQuery({
    queryKey: ['wf-projects', undefined, 0],
    queryFn: () => projectService.getProjects({ limit: 500 }),
    enabled: isStaff,
    staleTime: 0,
  });
  const staffHomeDataReady =
    isStaff &&
    staffClientsFetched &&
    staffProjectsFetched &&
    !staffClientsLoading &&
    !staffProjectsLoading;

  const clientHasProjects = (clientProjectsData?.pagination?.total ?? clientProjectsData?.data?.length ?? 0) > 0;
  const clientGateActive = isClient && !clientProjectsLoading && !clientHasProjects;

  const { data: agencyManagedClientsData, isLoading: agencyManagedClientsLoading } = useQuery({
    queryKey: ['wf-agency-managed-clients'],
    queryFn: () => clientService.getClients({ limit: 500, excludeAgencySelfProfile: true }),
    enabled: isAgency,
  });

  const { data: agencyAllClientsData, isLoading: agencyAllClientsLoading } = useQuery({
    queryKey: ['wf-agency-all-clients'],
    queryFn: () => clientService.getClients({ limit: 500 }),
    enabled: isAgency,
  });

  const agencySelfProfileClient = useMemo(() => {
    if (!isAgency || !user?.email) return undefined;
    return agencyAllClientsData?.data?.find(
      (c) => c.email?.toLowerCase() === user.email.toLowerCase(),
    );
  }, [isAgency, user?.email, agencyAllClientsData]);

  const { data: agencySelfProjectsData, isLoading: agencySelfProjectsLoading } = useQuery({
    queryKey: ['wf-agency-self-projects', agencySelfProfileClient?.id],
    queryFn: () =>
      projectService.getProjects({
        clientId: agencySelfProfileClient!.id,
        limit: 1,
      }),
    enabled: isAgency && Boolean(agencySelfProfileClient?.id),
  });

  const hasManagedClients =
    (agencyManagedClientsData?.pagination?.total ?? agencyManagedClientsData?.data?.length ?? 0) > 0;
  const hasLegacySelfProjects =
    (agencySelfProjectsData?.pagination?.total ?? agencySelfProjectsData?.data?.length ?? 0) > 0;

  const { data: agencyProjectsData, isLoading: agencyProjectsLoading } = useQuery({
    queryKey: ['wf-agency-project-count'],
    queryFn: () => projectService.getProjects({ limit: 1 }),
    enabled: isAgency,
  });

  const {
    agencyGateNoClients,
    agencyGateNoProjects,
    agencyGateActive,
    firstAgencyClientId,
    agencyOnboardingLoading,
  } = useMemo(() => {
    const agencyHasProjects =
      (agencyProjectsData?.pagination?.total ?? agencyProjectsData?.data?.length ?? 0) > 0;
    const onboardingLoading =
      isAgency &&
      (agencyManagedClientsLoading ||
        agencyAllClientsLoading ||
        (agencySelfProfileClient && agencySelfProjectsLoading) ||
        agencyProjectsLoading);
    const gateNoClients =
      isAgency && !onboardingLoading && !hasManagedClients && !hasLegacySelfProjects;
    const gateNoProjects =
      isAgency && !onboardingLoading && !gateNoClients && hasManagedClients && !agencyHasProjects;
    return {
      agencyGateNoClients: gateNoClients,
      agencyGateNoProjects: gateNoProjects,
      agencyGateActive: gateNoClients || gateNoProjects,
      firstAgencyClientId: agencyManagedClientsData?.data?.[0]?.id as string | undefined,
      agencyOnboardingLoading: onboardingLoading,
    };
  }, [
    isAgency,
    agencyManagedClientsLoading,
    agencyAllClientsLoading,
    agencySelfProfileClient,
    agencySelfProjectsLoading,
    hasManagedClients,
    hasLegacySelfProjects,
    agencyProjectsLoading,
    agencyProjectsData,
    agencyManagedClientsData,
  ]);

  const { data: pendingCountData } = useQuery({
    queryKey: ['pending-price-requests-count'],
    queryFn: () => priceChangeRequestService.getPendingCount(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const { data: pendingSitesStats } = useQuery({
    queryKey: ['pending-sites-stats'],
    queryFn: () => pendingSitesService.getStats(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const { data: drCalcPendingCountData } = useQuery({
    queryKey: ['dr-calculator-pending-count'],
    queryFn: () => competitorBacklinksService.getDrCalculatorPendingRequestsCount(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const { data: guestBlogArrivedCountData } = useQuery({
    queryKey: ['guest-blog-arrived-request-count'],
    queryFn: () => competitorBacklinksService.getUnseenGuestBlogArrivedCount(),
    enabled: isClient,
    refetchInterval: 45000,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-project-form'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: (isAdmin || isSuperAdmin || isAgency) && projectFormOpen,
  });

  const railItems = useMemo(() => {
    if (isClient) {
      return buildClientRailItems({
        guestBlogArrivedCount:
          typeof guestBlogArrivedCountData === 'number' ? guestBlogArrivedCountData : 0,
        navLocked: clientGateActive,
      });
    }
    if (isAgency) {
      return buildAgencyRailItems();
    }
    return buildAdminRailItems({
      hasPermission,
      pendingSitesCount: hasPermission('pending_sites') ? (pendingSitesStats?.data?.pending || 0) : 0,
      pendingPriceCount: hasPermission('price_change') ? (typeof pendingCountData === 'number' ? pendingCountData : 0) : 0,
    });
  }, [
    isClient,
    isAgency,
    hasPermission,
    clientGateActive,
    guestBlogArrivedCountData,
    pendingSitesStats,
    pendingCountData,
    drCalcPendingCountData,
  ]);

  // Home / project workflow: keep Clients & Projects open (including on refresh).
  // Other global menus (including Orders / notification deep-links) keep it collapsed.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const standalone = sp.get('standalone') === '1';
    const isHomeWorkflow =
      (!standalone && Boolean(urlProjectId)) ||
      location.pathname.includes('/project-workflow/');
    const isGlobalOrders =
      location.pathname.startsWith('/guest-blog-placements') &&
      (standalone || !urlProjectId);
    const isGlobalBrowseWithoutProject =
      (location.pathname.startsWith('/sites') ||
        location.pathname.startsWith('/planning') ||
        location.pathname.startsWith('/ai-suggestions') ||
        location.pathname.startsWith('/competitor-backlink-analysis') ||
        location.pathname.startsWith('/requested-sites')) &&
      !urlProjectId;

    if (isHomeWorkflow) {
      setPanelOpen(true);
      return;
    }
    if (
      isGlobalOrders ||
      isGlobalBrowseWithoutProject ||
      location.pathname.startsWith('/billing') ||
      location.pathname.startsWith('/invoices')
    ) {
      setPanelOpen(false);
    }
  }, [location.pathname, location.search, urlProjectId, setPanelOpen]);

  // Client gate: redirect away from non-dashboard routes
  useEffect(() => {
    if (!clientGateActive || clientProjectsLoading) return;
    if (urlProjectId || selectedProject) return;
    const allowed = location.pathname === '/dashboard';
    if (!allowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [clientGateActive, clientProjectsLoading, location.pathname, navigate, urlProjectId, selectedProject]);

  // Agency onboarding: client first, then project (like client login flow)
  useEffect(() => {
    if (!agencyGateActive || agencyOnboardingLoading) return;
    if (urlProjectId || selectedProject) return;
    const allowed = location.pathname.startsWith('/clients');
    if (!allowed) {
      navigate('/clients', { replace: true });
    }
  }, [agencyGateActive, agencyOnboardingLoading, location.pathname, navigate, urlProjectId, selectedProject]);

  useEffect(() => {
    if (!agencyGateNoProjects || !firstAgencyClientId || agencyOnboardingLoading) return;
    if (urlProjectId || selectedProject) return;
    const clientDetailPath = `/clients/${firstAgencyClientId}`;
    if (location.pathname !== clientDetailPath) {
      navigate(clientDetailPath, { state: { promptFirstProject: true }, replace: true });
    }
  }, [
    agencyGateNoProjects,
    firstAgencyClientId,
    agencyOnboardingLoading,
    location.pathname,
    navigate,
    urlProjectId,
    selectedProject,
  ]);

  // After login (or pending flag): open Home → top project → Explore Sites.
  // Wait for gate/data readiness before consuming the flag so Admin doesn't get stuck on /dashboard.
  useEffect(() => {
    if (!isClient && !isAgency && !isStaff) return;
    if (!hasPendingHomeLaunch()) return;

    // Already in a project URL (e.g. after first project creation) — consume flag, skip relaunch.
    if (urlProjectId) {
      consumePendingHomeLaunch();
      return;
    }

    if (isClient) {
      if (clientProjectsLoading || clientGateActive) return; // keep flag until ready / after first project
      if (!consumePendingHomeLaunch()) return;
      clearPreferDashboard();
      requestHomeLaunch();
      return;
    }

    if (isAgency) {
      if (agencyOnboardingLoading || agencyGateActive) return;
      if (!consumePendingHomeLaunch()) return;
      clearPreferDashboard();
      requestHomeLaunch();
      return;
    }

    // Admin / Super Admin — wait until clients + projects are loaded
    if (!staffHomeDataReady) return;
    if (!consumePendingHomeLaunch()) return;
    clearPreferDashboard();
    requestHomeLaunch();
  }, [
    isClient,
    isAgency,
    isStaff,
    urlProjectId,
    clientProjectsLoading,
    clientGateActive,
    agencyOnboardingLoading,
    agencyGateActive,
    staffHomeDataReady,
    requestHomeLaunch,
  ]);

  // Refresh / land on /dashboard with no project: auto-open first project Explore Sites
  // (unless user explicitly chose Dashboard in the sidebar).
  const dashboardAutoLaunchRef = useRef(false);
  useEffect(() => {
    if (location.pathname !== '/dashboard') {
      dashboardAutoLaunchRef.current = false;
      return;
    }
    if (!isStaff && !isClient && !isAgency) return;
    if (urlProjectId || selectedProject) return;
    if (prefersDashboard()) return;
    if (hasPendingHomeLaunch()) return; // handled by the effect above
    if (dashboardAutoLaunchRef.current) return;

    if (isClient && (clientProjectsLoading || clientGateActive)) return;
    if (isAgency && (agencyOnboardingLoading || agencyGateActive)) return;
    if (isStaff && !staffHomeDataReady) return;

    dashboardAutoLaunchRef.current = true;
    requestHomeLaunch();
  }, [
    isStaff,
    isClient,
    isAgency,
    location.pathname,
    urlProjectId,
    selectedProject,
    clientProjectsLoading,
    clientGateActive,
    agencyOnboardingLoading,
    agencyGateActive,
    staffHomeDataReady,
    requestHomeLaunch,
  ]);

  const createProjectMutation = useMutation({
    mutationFn: (data: CreateProjectData) => projectService.createProject(data),
    onSuccess: (project) => {
      completeProjectCreation(queryClient, project, {
        closeDialog: () => closeProjectForm(),
        focusClientInPanel,
        selectProject: (p, clientName, colorIndex) =>
          selectProject(p, clientName, colorIndex, 'overview'),
        clientName: isClient ? 'My account' : project.client?.name || 'Client',
        navigate,
        openAiSetup: true,
      });
      void queryClient.invalidateQueries({ queryKey: ['wf-client-project-count'] });
      void queryClient.invalidateQueries({ queryKey: ['wf-agency-project-count'] });
    },
  });

  const handleProjectSubmit = async (data: CreateProjectData | UpdateProjectData) => {
    setFormLoading(true);
    try {
      if (editingProject?.id) {
        const updated = await projectService.updateProject(
          editingProject.id,
          data as UpdateProjectData
        );
        completeProjectUpdate(queryClient, updated, {
          patchProjectInSelection,
          clientName: updated.client?.name,
        });
        void queryClient.invalidateQueries({ queryKey: ['project-reporting'] });
        closeProjectForm();
      } else {
        await createProjectMutation.mutateAsync(data as CreateProjectData);
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleArchiveProject = async (project: Project) => {
    await projectService.deleteProject(project.id);
    completeProjectDeletion(queryClient, project.id, {
      clearProject,
      selectedProjectId: selectedProject?.id,
      onCleared: () => {
        // Client and agency have no Projects menu. Stay in Clients & Projects
        // and open the next project (or the empty-project home if none remain).
        if (isClient || isAgency) {
          requestHomeLaunch();
          return;
        }
        navigate('/projects');
      },
    });
  };

  const projectSettingsLabel = useMemo(() => {
    const name = selectedProject?.projectName || 'Project Settings';
    return name.length > 18 ? `${name.slice(0, 16)}…` : name;
  }, [selectedProject?.projectName]);

  // The workflow step bar (Overview → … → Order Status) must ONLY show inside a
  // real project context. Several workflow steps share their path with global
  // menu pages (/sites, /guest-blog-placements, /planning), so we can't rely on
  // activeStep alone — a stale selectedProject would leak the tabs onto those
  // global pages. Genuine workflow routes always carry projectId in the URL.
  const hasProjectContext = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('standalone') === '1') return false;
    return Boolean(sp.get('projectId'));
  }, [location.search]);

  const breadcrumb = useMemo(() => {
    if (selectedProject && hasProjectContext) {
      const backToProjects =
        workflowEntryPoint === 'projects' ? (
          <>
            <button
              type="button"
              className="wf-breadcrumb-link"
              onClick={() => {
                clearProject();
                setPanelOpen(true);
                navigate('/projects');
              }}
            >
              Projects
            </button>
            <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
          </>
        ) : null;

      return (
        <>
          {backToProjects}
          <span style={{ color: 'var(--text-muted)' }}>{selectedProject.clientName}</span>
          <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
          <span className="cur">{selectedProject.projectName}</span>
        </>
      );
    }
    const clientDetailMatch = location.pathname.match(/^\/clients\/([^/]+)$/);
    if (clientDetailMatch) {
      return (
        <>
          <span style={{ color: 'var(--text-muted)' }}>Clients</span>
          <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>/</span>
          <span className="cur">Client details</span>
        </>
      );
    }
    const titles: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/orders': 'Transactions',
      '/publishers': 'Publishers',
      '/live-link-status': 'Backlink Health',
      '/pending-sites': 'Pending Sites',
      '/price-change-requests': 'Price Requests',
      '/users': 'Users',
      '/audit': 'Audit Logs',
      '/admin-billing': 'Billing',
      '/settings': 'Settings',
      '/competitor-backlink-site-requests': 'Competitor Backlinks',
      '/ai-suggestions': 'AI Suggestions',
      '/requested-sites': 'Requested Sites',
      '/competitor-backlink-analysis': 'Competitor Analysis',
      '/clients': 'Clients',
      '/projects': 'Projects',
      '/planning': 'Planning',
      '/sites': 'Guest Blog Sites',
      '/guest-blog-placements': 'Orders',
      '/archive': 'Archive',
      '/profile': 'My Profile',
    };
    const match = Object.keys(titles).find((p) => location.pathname.startsWith(p));
    return <span className="cur">{match ? titles[match] : 'Home'}</span>;
  }, [selectedProject, hasProjectContext, location.pathname, workflowEntryPoint, clearProject, navigate, setPanelOpen]);

  const renderMainContent = () => {
    // Pages with their own skeleton loaders should render immediately — don't flash
    // the global "Loading..." spinner first.
    const path = location.pathname;
    const pageHasOwnLoader =
      path.startsWith('/sites') ||
      path.startsWith('/guest-blog-placements') ||
      path.startsWith('/planning') ||
      path.startsWith('/project-workflow');

    if (
      !pageHasOwnLoader &&
      ((isClient && clientProjectsLoading) || agencyOnboardingLoading)
    ) {
      return (
        <div className="wf-content" style={{ paddingTop: 8 }}>
          {/* Silent placeholder — no spinner/text; pages with own loaders skip this */}
        </div>
      );
    }
    if (clientGateActive && location.pathname === '/dashboard') {
      return (
        <div className="wf-content">
          <div className="wf-client-gate">
            <h2>Create your first project</h2>
            <p>
              A project is required before you can explore sites, build orders, and place orders.
              Create your first project to get started.
            </p>
            <button
              type="button"
              className="wf-btn-create wf-btn-create-xl"
              onClick={() => openProjectForm()}
            >
              <AutoAwesome sx={{ fontSize: 22 }} />
              Create Project
            </button>
          </div>
        </div>
      );
    }
    return <div className="wf-content">{children}</div>;
  };

  const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase();
  const headerAvatarSrc = mediaUrl(user?.avatarUrl || user?.organizationLogoUrl);
  const showWallet = WALLET_ENABLED && (isClient || isAgency);
  const { data: walletSnap } = useQuery({
    queryKey: ['wallet', 'me'],
    queryFn: () => walletService.getWallet(),
    enabled: showWallet,
    staleTime: 30_000,
  });
  const walletLabel =
    walletSnap == null
      ? '—'
      : walletSnap.balance.toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
        });

  return (
    <div className="wf-shell">
      <IconRail items={railItems} disabled={clientGateActive || agencyGateActive} />
      <ClientProjectPanel
        open={panelOpen}
        onToggle={togglePanel}
        onCreateProject={openProjectForm}
        mode={isClient ? 'client' : 'admin'}
      />
      <div className="wf-main">
        <header className="wf-topbar">
          <button
            type="button"
            className="wf-tb-toggle"
            onClick={togglePanel}
            aria-label={panelOpen ? 'Collapse clients panel' : 'Open clients panel'}
            title={panelOpen ? 'Collapse clients & projects' : 'Open clients & projects'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" />
            </svg>
          </button>
          <div className="wf-breadcrumb">{breadcrumb}</div>
          <div className="wf-topbar-actions">
            {showWallet && (
              <button
                type="button"
                className="wf-tb-wallet"
                onClick={() => {
                  const here = `${location.pathname}${location.search}`;
                  const onBilling =
                    location.pathname.startsWith('/billing') || location.pathname.startsWith('/invoices');
                  if (!onBilling) rememberExploreSitesReturn(here);
                  navigate(isAgency ? '/invoices?tab=wallet' : '/billing?tab=wallet', {
                    state: onBilling ? undefined : { returnTo: here, returnLabel: 'Back to Explore Sites' },
                  });
                }}
                title={`Wallet balance ${walletLabel}`}
              >
                <AccountBalanceWallet sx={{ fontSize: 16 }} />
                <span className="wf-tb-wallet-amount">{walletLabel}</span>
              </button>
            )}
            <NotificationBell />
            {hasProjectContext && selectedProject && !clientGateActive && !agencyGateActive && (
              <button
                type="button"
                className="wf-tb-btn wf-tb-btn-acc"
                onClick={() => void openEditProjectForm()}
                title="Project settings"
              >
                <Settings sx={{ fontSize: 15 }} />
                <span className="wf-tb-settings-label">{projectSettingsLabel}</span>
              </button>
            )}
            {(!isClient || !clientGateActive) && !(isAgency && agencyGateActive) && (
              <button type="button" className="wf-btn-create" onClick={() => openProjectForm()} title="Create project">
                <AutoAwesome sx={{ fontSize: 14 }} />
                <span className="wf-btn-create-label">Create project</span>
              </button>
            )}
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar
                src={headerAvatarSrc}
                alt={user?.firstName || 'User'}
                sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', fontSize: 12 }}
              >
                {initials || 'U'}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled>
                <ListItemIcon>
                  <Avatar
                    src={headerAvatarSrc}
                    alt={user?.firstName || 'User'}
                    sx={{ width: 24, height: 24, fontSize: 11, background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
                  >
                    {initials || 'U'}
                  </Avatar>
                </ListItemIcon>
                <ListItemText>
                  {user?.firstName} {user?.lastName}
                  <Typography variant="caption" display="block">
                    {user?.email}
                  </Typography>
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  logout();
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </div>
        </header>
        {hasProjectContext && selectedProject && !clientGateActive && !agencyGateActive && activeStep !== null && (
          <ProjectWorkflowTabs />
        )}
        {renderMainContent()}
      </div>

      <ProjectForm
        open={projectFormOpen}
        onClose={closeProjectForm}
        onSubmit={handleProjectSubmit}
        project={editingProject}
        clients={
          isClient && user?.clientId
            ? [{ id: user.clientId, name: 'My account', email: user.email || '' }]
            : (clientsData?.data || []).map((c) => ({ id: c.id, name: c.name, email: c.email }))
        }
        loading={formLoading}
        isClientUser={isClient}
        userClientId={user?.clientId}
        canCreateClient={isAdmin || isAgency}
        fixedClientId={editingProject ? editingProject.clientId : projectFormClientId || undefined}
        onArchive={editingProject ? handleArchiveProject : undefined}
      />
    </div>
  );
};

export default WorkflowLayout;
